// TODO: Uncomment OkDisplay, userCreated, setUserCreated, okNotificationRef when lab has to pay subscription
import { Formik, Form, Field, FormikProps, FormikErrors } from "formik";
import { LabUserResponse, useUserService } from "../../services/users";
import { useEffect, useRef, useState } from "react";
import { LabSignUpFormData, TApiAddress, TCitiesData } from "../../lib/types/labs";
import { getBusinessTypes, getIndustryRoles } from "../../services/generalInfo";
import { getRegions, useAddress } from "../../services/regions";
import { TGenericGUIDName, TProcessStates } from "../../lib/types/general";
import { usePillInput } from "../common/pillInput";
import { useRouteNavigation } from "../../lib/common/routes";
import { PositionsCheckList } from "../common/positions-check-list";
import * as Yup from "yup";
import { emailRegex, isNumberFromString, phoneRegex } from "../../lib/common/validation";
import { Spinner } from "../common/spinner";
import { MESSAGES_MAP } from "../../services/i18n";
import { TEmployeeRole } from "../../lib/types/employee_roles";
import { TBusinessTypes } from "../../lib/types/business_types";
import { useLabs } from "../../services/laboratories";
import { evaluators, useWindowSizeMatch } from "../../lib/window-size-matcher";
import { DEVICES_SIZES_IN_PX, MIN_PASSWORD_LENGTH } from "../../lib/common/constants";
import { PasswordInput } from "../common/password-input";
import { usa_flag } from "../../images/flags";

type SignupLabValues = LabSignUpFormData & { confirmEmail: string };

export const SignupLabForm = () => {
    const [generalError, setGeneralError] = useState<string | undefined>(undefined);
    // const [userCreated, setUserCreated] = useState(false);
    const [businessTypes, setBusinessTypes] = useState<TBusinessTypes[]>([]);
    const [regions, setRegions] = useState<TGenericGUIDName[]>([]);
    const [positions, setPositions] = useState<TEmployeeRole[]>([]);
    const { getRoute, navigateToRoute } = useRouteNavigation();
    const [progress, setProgress] = useState(0);
    const [currentCard, setCurrentCard] = useState(1);
    const totalCards = 4;
    // const okNotificationRef = useRef(null);
    const signUpRef = useRef<HTMLDivElement>(null);
    const [workingState, setWorkingState] = useState<TProcessStates>("idle");
    const hobbies = usePillInput([]);
    const interests = usePillInput([]);
    const isSearching = useRef<boolean>(false);
    const [citiesList, setCitiesList] = useState<TCitiesData[]>([]);
    const [viewNewRegion, setViewNewRegion] = useState<string>();
    const [selectedCityState, setSelectedCityState] = useState("");
    const { getCities } = useAddress();
    const {
        public: { getVerifyEmployerIsDuplicate },
    } = useLabs();
    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });

    useEffect(() => {
        Promise.all([getRegions(), getIndustryRoles(), getBusinessTypes({})]).then(([dataRegions, dataIndustryRoles, dataBusinessTypes]) => {
            setRegions(dataRegions);
            setPositions(dataIndustryRoles);
            setBusinessTypes(dataBusinessTypes);
        }); //TODO: get a better error management
    }, []);

    const { configureNewLab, signUpUser } = useUserService();

    // const formValidationHandler = (values: LabSignUpFormData): FormikErrors<LabSignUpFormData> => {
    //     const errors: any = {};
    // if (!values.email) {
    //     errors.email = 'Required';
    // } else if (
    //     !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
    // ) {
    //     errors.email = 'Invalid email address';
    // }
    // if (!values.password) errors.password = "Required";
    // if (values.password && (!values.password2nd || (values.password !== values.password2nd))) errors.password2nd = "Passwords must match";
    // if (!values.labName) errors.labName = "Required";
    // if (!values.zip) errors.zip = "Required";
    // if (!values.contactName) errors.contactName = "Required";
    // if (!getPositions(values).length) errors.position_0 = "At least one is required";
    // if (!values.desiredRegion) errors.desiredRegion = "Required";
    // if (!values.accept) errors.accept = "Don't forget to agree to the terms and conditions.";

    //     return errors;
    // };
    const validationSchemas = [
        // Slide 1: Lab Data & TC
        Yup.object().shape({
            labName: Yup.string().required("Required"),
            contactName: Yup.string().required("Required"),
            phone: Yup.string()
                .matches(phoneRegex, "Enter a valid 10-digit US phone number.")
                .required("Required: Mobile phone number is required for SMS verification and account notifications."),
            accept: Yup.boolean().oneOf([true], "Please accept the terms and conditions"),
            smsConsent: Yup.boolean().oneOf([true], "SMS consent is required to continue"),
        }),
        // Slide 2: Region
        Yup.object().shape({
            zip: Yup.string()
                .test("is-number-from-string", "ZIP must be a number", (value) => (value ? isNumberFromString(value) : true))
                .required("Required"),
            address: Yup.object().shape({
                street: Yup.string().required("Required"),
                street_number: Yup.string(),
                city: Yup.string().required("Required"),
                state: Yup.string().required("Required"),
            }),
            desiredRegion: Yup.string().required("Required"),
        }),
        // Slide 3: Positions
        Yup.object().shape({
            yearsInBusiness: Yup.number()
                .nullable()
                .typeError("Must be a number")
                .integer("Must be a whole number")
                .min(0, "Must be a positive number")
                .max(299, "Can not be more than 299 years"),
            business_type_id: Yup.number().min(1, "You should select a business type").required("You should select a business type"),
            positions: Yup.array().of(Yup.number()).min(1, "At least one position is required").required("At least one position is required"),
        }),
        // Slide 4: Account Information
        Yup.object().shape({
            email: Yup.string().required("Required").matches(emailRegex, "Invalid email format"),
            confirmEmail: Yup.string().required("Required").oneOf([Yup.ref("email")], "Email addresses must match"),
            password: Yup.string().required("Required").min(MIN_PASSWORD_LENGTH, MESSAGES_MAP["auth/weak-password"]),
            password2nd: Yup.string()
                .oneOf([Yup.ref("password"), undefined], "Passwords must match")
                .required("Required"),
        }),
    ];

    const userValidationHandler = async (values: LabSignUpFormData): Promise<LabUserResponse> => {
        const signUpResponse = await signUpUser(values.email, values.password);
        if (signUpResponse.error) {
            setWorkingState("error");
            window.scrollTo({ top: 0 });
            setGeneralError(signUpResponse.error);
        }
        return signUpResponse;
    };

    const submitHandler = ({ confirmEmail: _confirmEmail, ...values }: SignupLabValues, { setSubmitting }: { setSubmitting: (val: boolean) => void }) => {
        setGeneralError(undefined);
        setTimeout(async () => {
            setWorkingState("working");
            const signUpResponse = await userValidationHandler(values);
            if (signUpResponse.error) {
                setSubmitting(false);
                return;
            }

            await configureNewLab({
                ...values,
                hobbies: hobbies.values,
                interests: interests.values,
                guid: signUpResponse.guid,
                positions: ((values.positions || []) as unknown as string[]).map((pid) => positions.find((p) => p.id === Number(pid)) as TEmployeeRole),
                password2nd: undefined,
            });
            // TODO: Uncomment setUserCreated when lab has to pay subscription
            // setUserCreated(true);
            localStorage.setItem("firstLogin", "true");
            localStorage.setItem("labName", values.labName || "");
            setWorkingState("done");
            setTimeout(() => {
                navigateToRoute("dentalLabAccount");
                // TODO: Uncomment okNotificationRef when lab has to pay subscription
                // (okNotificationRef.current as any).scrollIntoView({
                //     behavior: "smooth",
                //     block: "start",
                // });
                // (okNotificationRef.current as any).style.opacity = "1";
            }, 100);

            setSubmitting(false);
        }, 400);
    };
    // TODO: Uncomment OkDisplay when lab has to pay subscription
    // const OkDisplay = () => {
    //     const { navigateToRoute, navigateRouteClickHandler } =
    //         useRouteNavigation();

    //     setTimeout(() => {
    //         navigateToRoute("subscriptionSelection");
    //     }, 3000);

    //     return (
    //         <div
    //             ref={okNotificationRef}
    //             className="text-center"
    //             style={{ transition: "all 0.5s linear 0s", opacity: "0" }}
    //         >
    //             <i
    //                 className="fa-regular fa-circle-check text-success mb-4"
    //                 style={{ fontSize: "5rem" }}
    //             ></i>
    //             <h3 className="mb-4">🎉 Registration Successful!</h3>
    //             <a
    //                 className="text-info fs-5 fw-semibold"
    //                 href={getRoute("subscriptionSelection")}
    //                 onClick={(e) =>
    //                     navigateRouteClickHandler("subscriptionSelection", e)
    //                 }
    //             >
    //                 Let's Activate Your Plan ➡
    //             </a>
    //         </div>
    //     );
    // };

    const labNameRef = useRef<string>("");
    const streetNumberRef = useRef<string>("");
    const streetRef = useRef<string>("");
    const [checkCity, setCheckCity] = useState("");
    const [checkState, setCheckState] = useState("");

    const businessAndAddressExistHandle = async (): Promise<boolean> => {
        const isDuplicate = await getVerifyEmployerIsDuplicate(labNameRef.current, {
            street_number: streetNumberRef.current,
            street: streetRef.current,
            city: checkCity,
            state: checkState,
        });

        if (isDuplicate > 0) {
            setGeneralError(
                "We found a business with the same name on this address. Make sure your address is correct to create a new account for a different location.",
            );
            return true;
        }
        setGeneralError(undefined);
        return false;
    };

    const progressAction = async (direction: "next" | "prev", validateForm?: any) => {
        const errors = await validateForm();
        if (direction === "next" && currentCard < totalCards) {
            if (Object.keys(errors).length === 0) {
                const newCard = currentCard + 1;
                setCurrentCard(newCard);
                setProgress((newCard / totalCards) * 100);
            }
        } else if (direction === "prev" && currentCard > 1) {
            setGeneralError(undefined);
            const newCard = currentCard - 1;
            setCurrentCard(newCard);
            setProgress((newCard / totalCards) * 100);
        }

        signUpRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    const searchCityByName = async (cityInput: string) => {
        if (!cityInput) {
            setCitiesList([]);
            return;
        }
        if (isSearching.current) return;
        isSearching.current = true;
        const cityToSearch = cityInput.toLocaleLowerCase().trim();
        const results = await getCities(cityToSearch);
        isSearching.current = false;

        const newCityToSearch = cityInput.toLocaleLowerCase().trim();
        if (cityToSearch !== newCityToSearch) {
            searchCityByName(cityInput);
            return;
        }
        setCitiesList(
            results.data.map(
                (city): TCitiesData => ({
                    city_id: city.city_id,
                    city_name: city.city_name,
                    county: city.county,
                    state_id: city.state_id,
                    state_name: city.state_name,
                    state_abb: city.state_abb,
                    region_id: city.region_id,
                }),
            ),
        );
    };

    const regionList = (): TGenericGUIDName | undefined => {
        const newRegion = viewNewRegion;

        if (!newRegion) return undefined;

        const selectedRegion = regions.find((region) => {
            return newRegion.includes(region.guid);
        });
        return selectedRegion;
    };

    return (
        <div ref={signUpRef}>
            {/* TODO: Uncomment when lab has to pay subscription see closing brackets too.
            {userCreated ? (
                <OkDisplay />
            ) : ( */}
            <Formik
                initialValues={{
                    business_type_id: -1,
                    guid: "",
                    email: "",
                    confirmEmail: "",
                    password: "",
                    password2nd: "",
                    contactName: "",
                    labName: "",
                    phone: "",
                    zip: "",
                    yearsInBusiness: null,
                    desiredRegion: [],
                    positions: [],
                    accept: false,
                    smsConsent: false,
                    address: {
                        street_number: "",
                        street: "",
                        city: "",
                        state: "",
                    },
                }}
                validationSchema={validationSchemas[currentCard - 1]}
                onSubmit={submitHandler}
            >
                {({ isSubmitting, values, handleChange, validateForm, errors }) => (
                    <Form>
                        <div className="progress my-3 mx-auto signup-width px-0">
                            <div
                                className="progress-bar rounded"
                                style={{ width: `${progress}%` }}
                                role="progressbar"
                                aria-label="Lab Sign Up Progress"
                                aria-valuenow={progress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            >
                                {progress}%
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div id="labSignUpCarousel" data-touch="false" data-interval="false" className="carousel carousel-dark slide signup-width">
                                <div className="carousel-inner">
                                    {validationSchemas.map((validationSchema, index) => (
                                        <div key={index} className={`carousel-item ${currentCard === index + 1 ? "active" : ""}`}>
                                            <div className="card py-3">
                                                {index === 0 && (
                                                    <>
                                                        <h5 className="text-center py-3">👋 Welcome to WinWinGo!</h5>
                                                        <p className="text-center px-3">
                                                            {" "}
                                                            We're excited to have your business join our community!
                                                            <br />
                                                            Let's start with some basic information.{" "}
                                                        </p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col-6 col-md-3">
                                                                    <h6 className="m-0">
                                                                        <label className="form-label" htmlFor="Lab Name">
                                                                            Business Name *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col d-block d-md-none  mb-2">
                                                                    {errors.labName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.labName}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field name="labName">
                                                                        {({ form }: { form: FormikProps<LabSignUpFormData> }) => (
                                                                            <input
                                                                                id="Lab Name"
                                                                                type="text"
                                                                                className="form-control"
                                                                                maxLength={50}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue("labName", e.target.value);
                                                                                    labNameRef.current = e.target.value;
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.labName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.labName}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-6 col-md-3">
                                                                    <h6 className="m-0">
                                                                        <label htmlFor="Contact Name" className="form-label">
                                                                            Contact Name *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col d-block d-md-none mb-2">
                                                                    {errors.contactName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.contactName}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field
                                                                        id="Contact Name"
                                                                        name="contactName"
                                                                        type="text"
                                                                        className="form-control"
                                                                        maxLength={50}
                                                                    />
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.contactName ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.contactName}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-12 col-md-3 pe-0">
                                                                    <h6 className="m-0">
                                                                        <label htmlFor="phone" className="form-label mb-0">
                                                                            Mobile Phone Number *
                                                                        </label>{" "}
                                                                        <span className="form-text fw-light">Example: 5551234567</span>
                                                                    </h6>
                                                                </div>
                                                                <div className="col d-block d-md-none">
                                                                    {errors.phone ? (
                                                                        <div
                                                                            className="alert alert-warning py-1 px-2 mt-1 rounded-3 fw-bold"
                                                                            style={{ fontSize: "x-small" }}
                                                                        >
                                                                            <i className="fa-solid fa-circle-exclamation me-1"></i>
                                                                            {errors.phone}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <div className="input-group">
                                                                        <div className="input-group-text bg-light">
                                                                            <img src={usa_flag} alt="usa_flag" className="me-2 me-lg-0" width={20} />
                                                                            <span className="text-muted ms-md-2">+1</span>
                                                                        </div>
                                                                        <Field name="phone">
                                                                            {({ form }: { form: FormikProps<LabSignUpFormData> }) => (
                                                                                <input
                                                                                    id="phone"
                                                                                    name="phone"
                                                                                    type="tel"
                                                                                    className="form-control"
                                                                                    maxLength={10}
                                                                                    autoComplete="tel-national"
                                                                                    inputMode="numeric"
                                                                                    value={form.values.phone || ""}
                                                                                    onChange={(e) => {
                                                                                        form.setFieldValue(
                                                                                            "phone",
                                                                                            e.target.value.replace(/\D/g, "").slice(0, 10),
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.phone ? (
                                                                        <div
                                                                            className="alert alert-warning py-1 px-2 rounded-3 fw-bold"
                                                                            style={{ fontSize: "x-small" }}
                                                                        >
                                                                            <i className="fa-solid fa-circle-exclamation me-1"></i>
                                                                            {errors.phone}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            <hr />
                                                            <div className="row">
                                                                <div className="col-md-10">
                                                                    <div className="form-check">
                                                                        <input
                                                                            name="smsConsent"
                                                                            id="smsConsent"
                                                                            type="checkbox"
                                                                            className="form-check-input mt-1"
                                                                            checked={values.smsConsent}
                                                                            onChange={handleChange}
                                                                        />
                                                                        <label
                                                                            className="form-label lh-md"
                                                                            style={{ fontSize: "0.75rem" }}
                                                                            htmlFor="smsConsent"
                                                                        >
                                                                            By providing your phone number, you agree to receive SMS notifications from WinWinGo
                                                                            related to account activity, job connections, and verification codes. Message and
                                                                            data rates may apply depending on your carrier.
                                                                            <br />
                                                                            Reply STOP to unsubscribe.
                                                                            <br />
                                                                            See our{" "}
                                                                            <a
                                                                                href={getRoute("privacyPolicy")}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="text-primary"
                                                                            >
                                                                                Privacy Policy
                                                                            </a>{" "}
                                                                            and{" "}
                                                                            <a
                                                                                href={getRoute("termsOfUse")}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                className="text-primary"
                                                                            >
                                                                                Terms of Use
                                                                            </a>
                                                                            .
                                                                        </label>
                                                                    </div>
                                                                    {errors.smsConsent ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">{errors.smsConsent}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-12 col-md-9 pt-4">
                                                                    <div className="form-check">
                                                                        <input
                                                                            name="accept"
                                                                            id="accept_0"
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            checked={values.accept}
                                                                            onChange={handleChange}
                                                                            aria-describedby="acceptHelpBlock"
                                                                        />
                                                                        <label className="h6 form-label">
                                                                            I accept the
                                                                            <a
                                                                                className="text-primary"
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                href={getRoute("privacyPolicy")}
                                                                            >
                                                                                <strong> Privacy Policy </strong>
                                                                            </a>
                                                                            and
                                                                            <a
                                                                                className="text-primary"
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                href={getRoute("termsOfUse")}
                                                                            >
                                                                                <strong> Terms of Use</strong>
                                                                            </a>
                                                                            . *
                                                                        </label>
                                                                    </div>
                                                                    <span id="acceptHelpBlock" className="form-text text-muted"></span>
                                                                </div>
                                                                <div className="col mt-2">
                                                                    {errors.accept ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.accept}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {index === 1 && (
                                                    <>
                                                        <h5 className="text-center py-3">🌎 Where Is Your Business Located?</h5>
                                                        <p className="text-center px-3">
                                                            Help us pinpoint your business's location. Please enter your full address.
                                                            <br />
                                                            We'll automatically detect your region.
                                                            <br />
                                                            <span className="fw-bold">
                                                                If your business has multiple branches, please register each location separately.
                                                            </span>
                                                        </p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col-md-3">
                                                                    <h6 className="mb-0">
                                                                        <label className="form-label" htmlFor="street">
                                                                            Street Address *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-7 d-block d-md-none mb-2 mb-md-0">
                                                                    {typeof errors.address === "object" &&
                                                                        (errors.address as FormikErrors<TApiAddress>)?.street && (
                                                                            <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                                {(errors.address as FormikErrors<TApiAddress>).street}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field name="address.street">
                                                                        {({ form }: { form: FormikProps<LabSignUpFormData> }) => (
                                                                            <input
                                                                                id="street"
                                                                                type="text"
                                                                                className="form-control"
                                                                                maxLength={60}
                                                                                placeholder="e.g. 123 Main St."
                                                                                onChange={(e) => {
                                                                                    const inputValue = e.target.value;

                                                                                    form.setFieldValue("address.street", inputValue);
                                                                                    streetRef.current = inputValue;
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {typeof errors.address === "object" &&
                                                                        (errors.address as FormikErrors<TApiAddress>)?.street && (
                                                                            <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                                {(errors.address as FormikErrors<TApiAddress>).street}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-md-3">
                                                                    <h6 className="mb-0">
                                                                        <label className="form-label" htmlFor="street_number">
                                                                            Suite Number
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field name="address.street_number">
                                                                        {({ form }: { form: FormikProps<LabSignUpFormData> }) => (
                                                                            <input
                                                                                id="street_number"
                                                                                type="text"
                                                                                className="form-control"
                                                                                maxLength={10}
                                                                                placeholder="e.g. Suite 300"
                                                                                onChange={(e) => {
                                                                                    const inputValue = e.target.value;
                                                                                    form.setFieldValue("address.street_number", inputValue);
                                                                                    streetNumberRef.current = inputValue;
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-md-3">
                                                                    <h6 className="mb-0">
                                                                        <label className="form-label" htmlFor="City and State">
                                                                            City and State *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-7 d-block d-md-none mb-2 mb-md-0">
                                                                    {typeof errors.address === "object" &&
                                                                        (errors.address as FormikErrors<TApiAddress>)?.city && (
                                                                            <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                                {(errors.address as FormikErrors<TApiAddress>).city}
                                                                            </div>
                                                                        )}
                                                                </div>

                                                                <div className="col-md-5 mb-2 mb-md-0 position-relative">
                                                                    <Field name="address.city">
                                                                        {({ form }: { form: FormikProps<LabSignUpFormData> }) => (
                                                                            <>
                                                                                <input
                                                                                    className="form-control"
                                                                                    type="text"
                                                                                    placeholder="Find your city *"
                                                                                    value={selectedCityState}
                                                                                    autoComplete="new-city"
                                                                                    onChange={(e) => {
                                                                                        const inputValue = e.target.value;
                                                                                        setSelectedCityState(inputValue);
                                                                                        setCheckCity("");
                                                                                        setCheckState("");

                                                                                        form.setFieldValue("address.city", inputValue);
                                                                                        form.setFieldValue("address.state", "");
                                                                                        form.setFieldValue("desiredRegion", "");
                                                                                        searchCityByName(inputValue);
                                                                                    }}
                                                                                />

                                                                                <i className="fa-solid fa-xs errorIconInputs me-2 fa-magnifying-glass text-muted"></i>

                                                                                {citiesList.length > 0 && (
                                                                                    <ul className="w-100 mt-1 list-group searchResultStyle searchResultStyle__signUp rounded shadow">
                                                                                        {citiesList.map((v, idx) => (
                                                                                            <li
                                                                                                key={`${v.city_name}_${v.state_abb}_${idx}`}
                                                                                                className="list-group-item list-group-item-action py-2 px-3"
                                                                                                onClick={() => {
                                                                                                    form.setFieldValue("address.city", v.city_name);
                                                                                                    setCheckCity(v.city_name);
                                                                                                    form.setFieldValue("address.state", v.state_abb);
                                                                                                    setCheckState(v.state_abb);
                                                                                                    form.setFieldValue("desiredRegion", v.region_id);
                                                                                                    setCitiesList([]);
                                                                                                    setViewNewRegion(v.region_id);
                                                                                                    setSelectedCityState(`${v.city_name}, ${v.state_abb}`);
                                                                                                }}
                                                                                            >
                                                                                                {v.city_name}, {v.state_abb}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {typeof errors.address === "object" &&
                                                                        (errors.address as FormikErrors<TApiAddress>)?.city && (
                                                                            <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                                {(errors.address as FormikErrors<TApiAddress>).city}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-md-3">
                                                                    <h6 className="mb-0">
                                                                        <label className="form-label" htmlFor="Zip">
                                                                            ZIP Code *
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-7 d-block d-md-none mb-2 mb-md-0">
                                                                    {errors.zip ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">{errors.zip}</div>
                                                                    ) : null}
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <Field
                                                                        id="Zip"
                                                                        name="zip"
                                                                        type="text"
                                                                        className="form-control"
                                                                        maxLength={10}
                                                                        placeholder={"e.g. 90210"}
                                                                    />
                                                                </div>
                                                                <div className="col-md-4 d-none d-md-block">
                                                                    {errors.zip ? <div className="badge rounded-pill text-bg-warning">{errors.zip}</div> : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-md-3 mb-2">
                                                                    <h6 className="fw-bold">Region *</h6>
                                                                </div>
                                                                <div className="col-md-5 mb-2 mb-md-0">
                                                                    <div
                                                                        className="bg-light border rounded px-3 py-2"
                                                                        style={{
                                                                            minHeight: "2.5rem",
                                                                        }}
                                                                    >
                                                                        <p className="text-muted m-0">{selectedCityState ? regionList()?.name : ""}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {generalError ? (
                                                                <div className="alert alert-danger text-danger py-2 px-3 rounded-3 mb-2" role="alert">
                                                                    <i className="fa-solid fa-circle-exclamation me-2"></i>
                                                                    <small>{generalError}</small>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </>
                                                )}
                                                {index === 2 && (
                                                    <>
                                                        <h5 className="text-center py-3">💼 Your Business's Experience and Needs</h5>
                                                        <p className="text-center px-3">
                                                            We'd like to understand your business's staffing needs.
                                                            <br />
                                                            Please let us know how many years you've been in business and the positions you're seeking.
                                                        </p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col-md-4 py-md-2">
                                                                    <h6>
                                                                        <label htmlFor="Years in Business" className="form-label">
                                                                            Years in Business
                                                                        </label>
                                                                    </h6>
                                                                </div>
                                                                <div className="col-md-4 mb-2 mb-md-0">
                                                                    <Field
                                                                        id="Years in Business"
                                                                        name="yearsInBusiness"
                                                                        type="text"
                                                                        className="form-control"
                                                                        maxLength={3}
                                                                    />
                                                                </div>
                                                                <div className="col-md-3 mb-3">
                                                                    {errors.yearsInBusiness ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                            {errors.yearsInBusiness}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <h6 className="col-md-4 py-md-2">
                                                                    <label className="form-label">Business Type *</label>
                                                                </h6>
                                                                <div className="col-md-4 mb-2 mb-md-0">
                                                                    <Field className="form-select rounded px-3 py-2" name="business_type_id" as="select">
                                                                        <option disabled selected value="-1">
                                                                            select an option
                                                                        </option>
                                                                        {businessTypes.map((bt) => (
                                                                            <option
                                                                                key={bt.id}
                                                                                value={bt.id}
                                                                                selected={Number(bt.id) === Number(values.business_type_id)}
                                                                            >
                                                                                {bt.name}
                                                                            </option>
                                                                        ))}
                                                                    </Field>
                                                                    {errors.business_type_id ? (
                                                                        <div className="badge rounded-pill text-bg-warning text-wrap">
                                                                            {errors.business_type_id}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-12">
                                                                    <label className="h6 form-label">Positions We're Seeking *</label>
                                                                    <p>
                                                                        <small>
                                                                            You will be able to manage, add, or delete positions in your profile later too.
                                                                        </small>
                                                                    </p>
                                                                </div>
                                                                <div className="col-12 mb-3">
                                                                    {errors.positions ? (
                                                                        <div className="badge rounded-pill text-bg-warning">{errors.positions}</div>
                                                                    ) : null}
                                                                </div>
                                                                <PositionsCheckList fieldName="positions" roles={positions} />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {index === 3 && (
                                                    <>
                                                        <h5 className="text-center p-3">Almost Done! 🎉</h5>
                                                        <p className="text-center px-3">Let's choose your email and a secure password.</p>
                                                        <div className="card-body">
                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <label className="form-label small fw-semibold text-primary ms-2" htmlFor="Email">
                                                                        Email *
                                                                    </label>
                                                                    <Field id="Email" name="email" type="email" className="form-control mb-2" maxLength={254} />
                                                                    {errors.email && (
                                                                        <small className="badge rounded-pill text-bg-warning text-wrap">{errors.email}</small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <label className="form-label small fw-semibold text-primary ms-2" htmlFor="ConfirmEmail">
                                                                        Confirm Email *
                                                                    </label>
                                                                    <Field
                                                                        id="ConfirmEmail"
                                                                        name="confirmEmail"
                                                                        type="text"
                                                                        inputMode="email"
                                                                        className="form-control mb-2"
                                                                        maxLength={254}
                                                                        autoComplete="new-password"
                                                                        autoCapitalize="none"
                                                                        spellCheck={false}
                                                                        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => event.preventDefault()}
                                                                        onDrop={(event: React.DragEvent<HTMLInputElement>) => event.preventDefault()}
                                                                    />
                                                                    {errors.confirmEmail && (
                                                                        <small className="badge rounded-pill text-bg-warning text-wrap">{errors.confirmEmail}</small>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="row mb-3">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <PasswordInput
                                                                        label="Password *"
                                                                        value={values.password}
                                                                        onChange={(val) => handleChange("password")(val)}
                                                                        helperText={errors.password}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="row mb-4">
                                                                <div className="col mb-2 mb-md-0">
                                                                    <PasswordInput
                                                                        label="Confirm Password *"
                                                                        value={values.password2nd}
                                                                        onChange={(val) => handleChange("password2nd")(val)}
                                                                        helperText={errors.password2nd}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="row mx-1 mb-4">
                                                                {generalError ? (
                                                                    <div className="badge rounded-pill text-bg-danger text-wrap">{generalError}</div>
                                                                ) : null}
                                                            </div>

                                                            <div className=" lh-lg">
                                                                <h6>
                                                                    <i className="fa-solid fa-arrow-right me-2"></i>
                                                                    Next Step:{" "}
                                                                </h6>
                                                                <p>
                                                                    After signing up, you’ll be taken to your profile, where you can add your business logo and
                                                                    essential details.
                                                                    <br />
                                                                    <strong>Make your business stand out and connect with skilled dental talent today!</strong>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {!isSmallDevice.fulfilled && (
                            <div className="row gap-4 justify-content-center mt-3">
                                {currentCard > 1 && (
                                    <button
                                        className="btn btn-outline-dark col-md-4 col-lg-3"
                                        type="button"
                                        onClick={() => progressAction("prev", validateForm)}
                                    >
                                        <span className="" aria-hidden="true">
                                            Back
                                        </span>
                                        <span className="visually-hidden">Previous</span>
                                    </button>
                                )}
                                {currentCard < totalCards && (
                                    <button
                                        className="btn btn-primary col-md-7 col-lg-5"
                                        type="button"
                                        onClick={async () => {
                                            if (currentCard === 2) {
                                                const isDup = await businessAndAddressExistHandle();
                                                if (isDup) {
                                                    return;
                                                }
                                            }

                                            await progressAction("next", validateForm);
                                        }}
                                    >
                                        <span className="" aria-hidden="true">
                                            Next
                                        </span>
                                        <span className="visually-hidden">Next</span>
                                    </button>
                                )}
                                {currentCard === totalCards && (
                                    <button disabled={isSubmitting} name="submit" type="submit" className="btn btn-primary col-md-7 col-lg-5">
                                        {workingState === "working" ? <Spinner /> : "Find Dental Talent Now"}
                                    </button>
                                )}
                            </div>
                        )}
                        {isSmallDevice.fulfilled && (
                            <div className="row gap-4 justify-content-center mt-3 mx-0">
                                {currentCard < totalCards && (
                                    <button
                                        className="btn btn-primary btn-lg rounded-2 shadow position-relative px-5"
                                        type="button"
                                        onClick={async () => {
                                            if (currentCard === 2) {
                                                const isDup = await businessAndAddressExistHandle();
                                                if (isDup) {
                                                    return;
                                                }
                                            }

                                            await progressAction("next", validateForm);
                                        }}
                                    >
                                        <span className=" position-relative" aria-hidden="true">
                                            Next
                                        </span>
                                        <i className="fa-solid fa-circle-right fa-lg position-absolute end-0 top-50 translate-middle-y me-3"></i>
                                        <span className="visually-hidden">Next</span>
                                    </button>
                                )}
                                {currentCard === totalCards && (
                                    <button disabled={isSubmitting} name="submit" type="submit" className="btn btn-primary btn-lg rounded-2 shadow">
                                        {workingState === "working" ? <Spinner /> : "Find Dental Talent Now"}
                                    </button>
                                )}
                                {currentCard > 1 && (
                                    <button
                                        className="btn btn-light shadow position-relative px-5"
                                        type="button"
                                        onClick={() => progressAction("prev", validateForm)}
                                    >
                                        <i className="fa-solid fa-circle-left fa-lg position-absolute start-0 top-50 translate-middle-y ms-3"></i>
                                        <span className="" aria-hidden="true">
                                            Back
                                        </span>
                                        <span className="visually-hidden">Previous</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
            {/* )} */}
        </div>
    );
};
